/**
 * Smart Run Planner Types
 * For athletes/runners to plan safe, healthy routes
 */

export interface RunPlanRequest {
  // Location
  startPoint: {
    lat: number;
    lng: number;
    address?: string;
  };
  endPoint?: {
    lat: number;
    lng: number;
    address?: string;
  };

  // Distance
  targetDistance: number; // in meters
  distanceTolerance: number; // flexibility in meters

  // Preferences (0-100 priority weight)
  preferences: RunPreferences;

  // Schedule
  scheduledTime?: {
    date: string;
    time: string;
  };

  // Social
  participants?: string[];
  isPublic?: boolean;
}

export interface RunPreferences {
  airQuality: number;      // Prioritize clean air zones (0-100)
  minimalCrossings: number; // Avoid road crossings (0-100)
  greenSpaces: number;      // Prefer parks, trails (0-100)
  elevation: 'flat' | 'moderate' | 'hilly';
  loopRoute: boolean;       // Return to start
  waterStops: boolean;      // Include water fountains
  lighting: boolean;        // Well-lit paths (for evening runs)
  shade: boolean;           // Tree coverage (for hot days)
}

export interface GeneratedRoute {
  id: string;
  name: string;
  recommended: boolean;

  // Route data
  path: Array<{ lat: number; lng: number }>;
  distance: number; // meters
  estimatedDuration: {
    walkingPace: number;  // minutes
    joggingPace: number;
    runningPace: number;
  };

  // Safety & Health Scores
  scores: RouteScores;

  // Detailed segments
  segments: RouteSegment[];

  // Points of interest
  waypoints: RouteWaypoint[];

  // Warnings
  warnings: RouteWarning[];

  // Weather & AQ forecast for scheduled time
  forecast?: RouteForecast;
}

export interface RouteScores {
  overall: number;        // 0-100
  airQuality: number;     // 0-100
  safety: number;         // 0-100 (crossings, traffic, lighting)
  greenCoverage: number;  // percentage
  neuralIndexImpact: number; // estimated cognitive benefit
}

export interface RouteSegment {
  startIndex: number;
  endIndex: number;
  type: 'park' | 'sidewalk' | 'trail' | 'crossing' | 'residential' | 'waterfront';
  airQuality: number;
  distance: number;
  notes?: string;
}

export interface RouteWaypoint {
  lat: number;
  lng: number;
  type: 'water_fountain' | 'restroom' | 'crossing' | 'landmark' | 'warning' | 'start' | 'checkpoint';
  name: string;
  description?: string;
  kmMark?: number;
}

export interface RouteWarning {
  type: 'air_quality' | 'traffic' | 'construction' | 'unlit' | 'steep';
  severity: 'low' | 'medium' | 'high';
  message: string;
  location?: { lat: number; lng: number };
}

export interface RouteForecast {
  temperature: number;
  humidity: number;
  airQualityIndex: number;
  uvIndex: number;
  windSpeed: number;
  recommendation: string;
}

export interface ScheduledRun {
  id: string;
  route: GeneratedRoute;
  scheduledTime: Date;
  createdBy: string;
  participants: RunParticipant[];
  status: 'draft' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  chatMessages?: RunChatMessage[];
}

export interface RunParticipant {
  id: string;
  name: string;
  avatar?: string;
  status: 'invited' | 'confirmed' | 'declined' | 'maybe';
}

export interface RunChatMessage {
  id: string;
  participantId: string;
  message: string;
  timestamp: Date;
}

// Distance presets
export const DISTANCE_PRESETS = [
  { label: '3K', value: 3000, description: 'Quick run' },
  { label: '5K', value: 5000, description: 'Standard' },
  { label: '10K', value: 10000, description: 'Distance' },
  { label: '21K', value: 21097, description: 'Half marathon' },
  { label: '42K', value: 42195, description: 'Marathon' },
] as const;

// Default preferences
export const DEFAULT_PREFERENCES: RunPreferences = {
  airQuality: 80,
  minimalCrossings: 70,
  greenSpaces: 60,
  elevation: 'flat',
  loopRoute: true,
  waterStops: false,
  lighting: false,
  shade: false,
};
